import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GraduationCap, UserRound } from "lucide-react";

import Button from "../../../../shared/ui/button/Button";
import FilterSelect from "../../../../shared/ui/form/FilterSelect";
import Label from "../../../../shared/ui/form/Label";
import { Modal } from "../../../../shared/ui/modal";
import { useStatementDownload, type StatementPeriod } from "../../application/usePractitionerReport";
import { cn } from "../../../../shared/ui/cn";
import {
    subjectKey,
    subjectName,
    type ReportCourseOption,
    type ReportScope,
    type StatementFormat,
    type ReportSubjectOption,
} from "../../domain/PractitionerReport";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    scope: ReportScope;
    /** `YYYY-MM` to start from: the month the report is looking at. */
    initialMonth: string;
    /** Who can be picked: those with sessions in the report's window. */
    subjects: ReportSubjectOption[];
    /** The subject already picked on the page, `"<kind>:<id>"` or empty. */
    initialSubject: string;
    /** The course picked on the page, where the download starts; empty for all. */
    course?: string;
    /** The courses that can be picked; none outside training centres. */
    courses?: ReportCourseOption[];
    /** The page is about one patient or student: no picker, always them. */
    fixedSubject?: boolean;
    /** A report across professionals filtered to one: their id and name. */
    therapist?: { id: string; name: string };
}

const FIELD = "field-base field-default h-11 w-full py-2.5 pl-4 pr-4 text-content-primary";
const PERIODS: StatementPeriod[] = ["month", "year"];
const FORMATS: StatementFormat[] = ["pdf", "xlsx"];
/** The years offered: this one and the five before it. */
const YEARS_BACK = 5;

/**
 * A month or a whole year as the PDF statement: everybody, or one patient,
 * couple or group. The layout is the backend's and always the same; here only
 * the period and who it is about are chosen.
 */
export default function MonthStatementDialog({
    isOpen,
    onClose,
    scope,
    initialMonth,
    subjects,
    initialSubject,
    course: initialCourse = "",
    courses = [],
    fixedSubject = false,
    therapist,
}: Props) {
    const { t, i18n } = useTranslation();
    const { download, downloading, error, reset } = useStatementDownload(scope);
    const [period, setPeriod] = useState<StatementPeriod>("month");
    const [format, setFormat] = useState<StatementFormat>("pdf");
    const [month, setMonth] = useState(initialMonth);
    const [year, setYear] = useState(initialMonth.slice(0, 4));
    const [subject, setSubject] = useState(initialSubject);
    const [course, setCourse] = useState(initialCourse);
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: YEARS_BACK + 1 }, (_, i) => String(currentYear - i));

    useEffect(() => {
        if (!isOpen) return;
        setPeriod("month");
        setMonth(initialMonth);
        setYear(initialMonth.slice(0, 4));
        setSubject(initialSubject);
        setCourse(initialCourse);
        reset();
    }, [isOpen, initialMonth, initialSubject, initialCourse, reset]);

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        const value = period === "month" ? month : year;
        if (!(period === "month" ? /^\d{4}-\d{2}$/ : /^\d{4}$/).test(value)) return;
        try {
            await download({ period, value, subject, course, therapist: therapist?.id, locale: i18n.language, format });
            onClose();
        } catch {
            // Shown below from the mutation's own state.
        }
    };

    const groupOf = (count: number) => t("reports.groupOf", { count });

    return (
        <Modal isOpen={isOpen} onClose={onClose} ariaLabel={t("reports.download.title")} className="max-w-lg p-6">
            <form onSubmit={submit} className="space-y-5">
                <div>
                    <h2 className="text-lg font-semibold text-content-primary">
                        {t("reports.download.title")}
                    </h2>
                    <p className="mt-1 text-sm text-content-secondary">{t("reports.download.hint")}</p>
                    {therapist && (
                        <p className="mt-2 text-sm font-medium text-content-primary">
                            {t("reports.download.onlyMentor", { name: therapist.name })}
                        </p>
                    )}
                </div>

                <Segmented
                    legend={t("reports.download.period")}
                    name="statement-period"
                    options={PERIODS.map((option) => ({ value: option, label: t(`reports.download.periods.${option}`) }))}
                    value={period}
                    onChange={(value) => setPeriod(value as StatementPeriod)}
                />

                {period === "month" ? (
                    <div className="space-y-1.5">
                        <Label htmlFor="statement-month">{t("reports.download.month")}</Label>
                        <input
                            id="statement-month"
                            type="month"
                            required
                            value={month}
                            onChange={(event) => setMonth(event.target.value)}
                            className={FIELD}
                        />
                    </div>
                ) : (
                    <div className="space-y-1.5">
                        <Label htmlFor="statement-year">{t("reports.download.year")}</Label>
                        <select
                            id="statement-year"
                            value={year}
                            onChange={(event) => setYear(event.target.value)}
                            className={FIELD}
                        >
                            {(years.includes(year) ? years : [year, ...years]).map((option) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                <Segmented
                    legend={t("reports.download.format")}
                    name="statement-format"
                    options={FORMATS.map((option) => ({ value: option, label: t(`reports.download.formats.${option}`) }))}
                    value={format}
                    onChange={(value) => setFormat(value as StatementFormat)}
                />

                {/* A training centre's statement can be one course's alone. */}
                {courses.length > 0 && (
                    <div className="space-y-1.5">
                        <Label>{t("reports.course")}</Label>
                        <FilterSelect
                            options={courses.map((option) => ({ value: option.id, label: option.name }))}
                            value={course}
                            onChange={(value) => setCourse(value as string)}
                            placeholder={t("reports.allCourses")}
                            searchPlaceholder={t("reports.searchCourse")}
                            icon={<GraduationCap className="h-5 w-5" />}
                            className="text-sm"
                        />
                    </div>
                )}

                {/* The same searchable picker as the report's own filter: a
                    centre's list of students runs long. Empty is everybody. */}
                {!fixedSubject && subjects.length > 0 && (
                <div className="space-y-1.5">
                    <Label>{t("reports.patient")}</Label>
                    <FilterSelect
                        options={subjects.map((option) => ({
                            value: subjectKey(option.type, option.id),
                            label: subjectName(option, groupOf),
                        }))}
                        value={subject}
                        onChange={(value) => setSubject(value as string)}
                        placeholder={t("reports.everyone")}
                        searchPlaceholder={t("reports.searchPatient")}
                        icon={<UserRound className="h-5 w-5" />}
                        className="text-sm"
                    />
                </div>
                )}

                {error && (
                    <p role="alert" className="text-sm text-status-critical">
                        {t("reports.download.error")}
                    </p>
                )}

                <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                    <Button type="button" variant="outline" size="sm" onClick={onClose}>
                        {t("reports.center.cancel")}
                    </Button>
                    <Button type="submit" size="sm" loading={downloading} disabled={downloading}>
                        {t("reports.download.submit")}
                    </Button>
                </div>
            </form>
        </Modal>
    );
}

/** A choice of two or three, as radio buttons drawn as one segmented control. */
function Segmented({
    legend,
    name,
    options,
    value,
    onChange,
}: {
    legend: string;
    name: string;
    options: { value: string; label: string }[];
    value: string;
    onChange: (value: string) => void;
}) {
    return (
        <fieldset className="space-y-1.5">
            <legend className="mb-1.5 text-sm font-medium text-content-primary">{legend}</legend>
            <div
                className="grid gap-1 rounded-lg border border-gray-300 p-1 dark:border-gray-700"
                style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
            >
                {options.map((option) => (
                    <label
                        key={option.value}
                        className={cn(
                            "flex min-h-[40px] cursor-pointer items-center justify-center rounded-md text-sm font-medium transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-brand-500",
                            value === option.value
                                ? "bg-brand-500 text-white"
                                : "text-content-secondary hover:bg-gray-100 dark:hover:bg-white/5"
                        )}
                    >
                        <input
                            type="radio"
                            name={name}
                            value={option.value}
                            checked={value === option.value}
                            onChange={() => onChange(option.value)}
                            className="sr-only"
                        />
                        {option.label}
                    </label>
                ))}
            </div>
        </fieldset>
    );
}
